/**
 * GA4 client identifiers — read from gtag on the browser and pass them to
 * the backend so the server-side `generate_lead` event stitches with the
 * same GA4 session the visitor was seen in (correct source/medium
 * attribution).
 *
 * Backwards-compatible: returns { ga_client_id: null, ga_session_id: null }
 * on any error (ad-blocker, gtag not loaded, cookie blocked). The server
 * falls back to a synthetic client_id (hash of email) in that case, so the
 * conversion is never lost — attribution just gets a bit fuzzier.
 *
 * Usage:
 *   const gaIds = await getGa4Ids();
 *   await axios.post('/api/enquiries/', { ...payload, ...gaIds });
 */

const GA4_MEASUREMENT_ID = 'G-8Y5QWLMQN8';

function readCookieClientId() {
  try {
    const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return null;
    // The _ga cookie value format is "GA1.1.<random>.<timestamp>" — GA4's
    // client_id is the last two dot-separated segments joined with a dot.
    const parts = match[1].split('.');
    if (parts.length < 4) return null;
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
  } catch {
    return null;
  }
}

function getFromGtag(field) {
  return new Promise((resolve) => {
    const gtag = typeof window !== 'undefined' && window.gtag;
    if (!gtag) return resolve(null);
    let done = false;
    // Timeout guard — gtag.get sometimes never fires if analytics is blocked
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(null);
      }
    }, 350);
    try {
      gtag('get', GA4_MEASUREMENT_ID, field, (value) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value || null);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

export async function getGa4Ids() {
  try {
    const [client_id, session_id] = await Promise.all([
      getFromGtag('client_id'),
      getFromGtag('session_id'),
    ]);
    return {
      ga_client_id: client_id || readCookieClientId(),
      ga_session_id: session_id || null,
    };
  } catch {
    return { ga_client_id: null, ga_session_id: null };
  }
}
